import React, { useMemo } from "react";
import { useList, useTranslate } from "@refinedev/core";
import { Maybe, SessionStatistics } from "types/";
import dayjs from "dayjs";
import { AnalyticsNestedBarChart } from "./AnalyticsNestedBarChart";
import { buildDateFilters } from "./utils/";
import { analyticsTheme } from "./analyticsTheme";
import { ANALYTICS_STALE_TIME } from "./constants/constants";

const T_PREFIX =
  "media.projects.analytics.dashboard.summaryCards.sessionsChart";

interface SessionsChartProps {
  projectId: string;
  dateRange: {
    startDate: Maybe<dayjs.Dayjs>;
    endDate: Maybe<dayjs.Dayjs>;
  };
}

export const SessionsChart: React.FC<SessionsChartProps> = ({
  projectId,
  dateRange,
}) => {
  const t = useTranslate();
  const {
    data: sessionStatistics,
    isLoading,
    isFetching,
  } = useList<SessionStatistics>({
    resource: `analytics/projects/${projectId}/sessions/v2`,
    config: {
      filters: buildDateFilters(dateRange),
    },
    queryOptions: {
      enabled: !!projectId && !!dateRange.startDate && !!dateRange.endDate,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const sessionsData = useMemo(
    () => sessionStatistics?.data ?? [],
    [sessionStatistics]
  );

  const filledData = useMemo<SessionStatistics[]>(() => {
    if (sessionsData.length === 0) return sessionsData;

    const byDate = new Map(
      sessionsData.map((item) => [dayjs(item.date).format("YYYY-MM-DD"), item])
    );

    const first = sessionsData.reduce((a, b) =>
      dayjs(b.date).isBefore(dayjs(a.date)) ? b : a
    );
    const last = sessionsData.reduce((a, b) =>
      dayjs(b.date).isAfter(dayjs(a.date)) ? b : a
    );
    let cursor = dayjs(first.date).startOf("day");
    const lastDay = dayjs(last.date).startOf("day");

    const days: SessionStatistics[] = [];
    while (!cursor.isAfter(lastDay, "day")) {
      const key = cursor.format("YYYY-MM-DD");
      days.push(
        byDate.get(key) ?? {
          date: cursor.toISOString(),
          sessions: 0,
          unique_visitors: 0,
          average_duration: 0,
          chat_interactions: 0,
        }
      );
      cursor = cursor.add(1, "day");
    }
    return days;
  }, [sessionsData]);

  const labels = useMemo(
    () => filledData.map((item) => dayjs(item.date).format("MMM D")),
    [filledData]
  );

  const sessionsSeries = useMemo(
    () => ({
      label: t(`${T_PREFIX}.sessions`),
      data: filledData.map((item) => item.sessions),
      color: analyticsTheme.datasetBlue,
    }),
    [filledData, t]
  );

  const visitorsSeries = useMemo(
    () => ({
      label: t(`${T_PREFIX}.visitors`),
      data: filledData.map((item) => item.unique_visitors),
      color: analyticsTheme.datasetGreen,
    }),
    [filledData, t]
  );

  return (
    <AnalyticsNestedBarChart
      title={t(`${T_PREFIX}.title`)}
      subtitle={t(`${T_PREFIX}.subtitle`)}
      labels={labels}
      wholeSeries={sessionsSeries}
      partSeries={visitorsSeries}
      loading={isLoading || isFetching}
    />
  );
};
